import { Link } from 'react-router-dom'
import type { Frequency, RecurringHistory, RecurringHistoryEntry } from '@/api/recurring'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { formatMonth, formatShortDate } from '@/lib/formatDate'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { transactionsLink } from '@/lib/transactionsLink'
import { ROUTES } from '@/routes/routes'

const MONTHS_BACK: Record<Frequency, number> = {
  WEEKLY: 0,
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12,
}

function previousDue(nextDueDate: string, frequency: Frequency): string {
  const [y, m, d] = nextDueDate.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  if (frequency === 'WEEKLY') date.setDate(date.getDate() - 7)
  else date.setMonth(date.getMonth() - MONTHS_BACK[frequency])
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

// Payments made before reminders were linked (#377) exist but can't be shown here.
function hasUnlinkedPast(history: RecurringHistory, nextDueDate: string, frequency: Frequency) {
  const created = history.createdAt.slice(0, 10)
  const oldest = history.entries.at(-1)?.date
  return created < previousDue(oldest ?? nextDueDate, frequency)
}

export function RecurringHistoryList({
  history,
  nextDueDate,
  frequency,
}: {
  history: RecurringHistory
  nextDueDate: string
  frequency: Frequency
}) {
  const { format } = useFormatCurrency()
  const footer = hasUnlinkedPast(history, nextDueDate, frequency) && (
    <p className="mt-3 text-xs text-[var(--fl-text-2)]">
      Earlier payments aren&apos;t linked to this reminder, so they don&apos;t show here.
    </p>
  )

  if (history.entries.length === 0) {
    return (
      <div>
        <p className="py-3 text-sm text-[var(--fl-text-2)]">No payments recorded yet.</p>
        {footer}
      </div>
    )
  }

  const paidOnly = history.entries.filter((e) => e.kind === 'PAID')
  // Against what was expected at the time; older payments never recorded that, so they keep
  // the comparison with the payment before.
  const changeOf = (entry: RecurringHistoryEntry): { diff: number; than: string } | null => {
    if (entry.amount == null) return null
    let base = entry.expectedAmount
    let than = 'than expected'
    if (base == null) {
      const before = paidOnly[paidOnly.indexOf(entry) + 1]
      if (!before || before.amount == null) return null
      base = before.amount
      than = 'than before'
    }
    const diff = fromPaise(toPaise(entry.amount) - toPaise(base))
    return diff === 0 ? null : { diff, than }
  }

  const startsMonth = history.entries.map(
    (e, i) => i === 0 || e.date.slice(0, 7) !== history.entries[i - 1].date.slice(0, 7)
  )
  return (
    <div className="text-[13.5px]">
      {history.entries.map((entry, idx) => {
        const entryMonth = entry.date.slice(0, 7)
        const heading = startsMonth[idx]
        const change = entry.kind === 'PAID' ? changeOf(entry) : null
        return (
          <div key={`${entry.kind}-${entry.date}-${idx}`}>
            {heading && (
              <p className="mt-3 mb-0.5 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--fl-text-3)]">
                {formatMonth(entryMonth)}
              </p>
            )}
            <div className="flex items-center gap-2.5 border-t border-[var(--fl-divider)] py-2">
              <span
                aria-hidden="true"
                className={
                  'h-2 w-2 shrink-0 rounded-full ' +
                  (entry.kind === 'PAID' ? 'bg-[var(--fl-income)]' : 'bg-[var(--fl-warning)]')
                }
              />
              <div className="min-w-0 flex-1">
                {entry.kind === 'PAID' ? (
                  <>
                    <p className="text-[var(--fl-text-1)]">
                      Paid {formatShortDate(entry.date)}
                      {change != null && (
                        <span className="ml-1.5 rounded-md bg-[var(--fl-surface-subtle)] px-1.5 py-px text-[11px] font-semibold text-[var(--fl-text-2)]">
                          {format(Math.abs(change.diff))} {change.diff > 0 ? 'more' : 'less'}{' '}
                          {change.than}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[var(--fl-text-2)]">
                      {entry.principal != null && entry.interest != null
                        ? `Principal ${format(entry.principal)} · interest ${format(entry.interest)} · `
                        : entry.accountName
                          ? `${entry.accountName} · `
                          : ''}
                      {/* Replaces the detail view's own history entry, so Back skips straight to the list. */}
                      <Link
                        replace
                        className="underline-offset-2 hover:underline"
                        to={transactionsLink(
                          { date: entry.date, financialAccountId: entry.accountId ?? undefined },
                          { back: ROUTES.APP.RECURRING }
                        )}
                      >
                        open in Transactions ›
                      </Link>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[var(--fl-text-1)]">Missed {formatShortDate(entry.date)}</p>
                    <p className="text-xs text-[var(--fl-text-2)]">
                      {entry.reason ? `“${entry.reason}”` : 'No reason given'}
                    </p>
                  </>
                )}
              </div>
              <span
                className={
                  'shrink-0 font-semibold tabular-nums ' +
                  (entry.amount == null ? 'text-[var(--fl-text-3)]' : 'text-[var(--fl-text-1)]')
                }
              >
                {entry.amount == null ? '—' : format(entry.amount)}
              </span>
            </div>
          </div>
        )
      })}
      {footer}
    </div>
  )
}
